/**
 * Pipeline Service — triggers and polls the Step Functions scan pipeline.
 */

import sentinelNetwork from './sentinelNetwork';

export interface PipelineToolStatus {
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  completedAt?: string;
  error?: string;
}

export interface PipelineStatus {
  status: 'none' | 'running' | 'completed' | 'failed';
  executionId?: string;
  triggeredAt?: string;
  completedAt?: string;
  triggeredBy?: string;
  tools: Record<string, PipelineToolStatus>;
  scoring: { status: string };
}

export interface TriggerPipelineResponse {
  executionId: string;
  status: string;
  alreadyRunning: boolean;
  triggeredAt: string;
}

/**
 * Trigger a scan pipeline for a project. Returns immediately —
 * poll `getPipelineStatus` for progress.
 */
export async function triggerScanPipeline(
  appId: string,
  triggeredBy: string = 'auto-refresh',
): Promise<TriggerPipelineResponse> {
  const res = await sentinelNetwork.post('/scan-pipeline/trigger', { appId, triggeredBy });
  return res.data;
}

/**
 * Get the latest pipeline status for a project. Lean — no reports or scores.
 */
export async function getPipelineStatus(appId: string): Promise<PipelineStatus> {
  const res = await sentinelNetwork.get(`/scan-pipeline/status/${appId}`);
  return res.data;
}

/**
 * Poll pipeline status until it completes (or times out).
 *
 * @param appId Project ID
 * @param onUpdate Called with each status update
 * @param intervalMs Polling interval (default 4s)
 * @param timeoutMs Maximum polling duration (default 5 min)
 * @returns Final status
 */
export async function pollPipelineStatus(
  appId: string,
  onUpdate?: (status: PipelineStatus) => void,
  intervalMs = 4000,
  timeoutMs = 5 * 60 * 1000,
): Promise<PipelineStatus> {
  const start = Date.now();
  return new Promise<PipelineStatus>((resolve) => {
    const poll = setInterval(async () => {
      try {
        const status = await getPipelineStatus(appId);
        onUpdate?.(status);
        if (status.status !== 'running' || Date.now() - start > timeoutMs) {
          clearInterval(poll);
          resolve(status);
        }
      } catch {
        if (Date.now() - start > timeoutMs) {
          clearInterval(poll);
          resolve({ status: 'failed', tools: {}, scoring: { status: 'failed' } });
        }
      }
    }, intervalMs);
  });
}
